import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { Check, Circle, ChevronRight, NotebookPen, User as UserIcon, BookOpen, X, Sparkles } from "lucide-react";

const ICONS = { tastings: NotebookPen, profile: UserIcon, course: BookOpen };

const LABELS = {
  tastings: {
    fr: { title: "Enregistrer 3 dégustations", body: "geoVINUM AI structure vos notes (robe, nez, bouche, score)." },
    en: { title: "Log 3 tastings",              body: "geoVINUM AI structures your notes (color, nose, palate, score)." },
  },
  profile: {
    fr: { title: "Créer votre profil aromatique", body: "Signature gustative + régions et cépages favoris." },
    en: { title: "Create your taste profile",     body: "Taste signature + favourite regions and grapes." },
  },
  course: {
    fr: { title: "Terminer un parcours Academy", body: "Débloque un badge et un tampon sur votre Passport." },
    en: { title: "Complete an Academy journey",  body: "Unlocks a badge and a stamp on your Passport." },
  },
};

const HEADER = {
  fr: { title: "Bienvenue chez geoVINUM", sub: "3 étapes pour prendre en main votre abonnement.", dismiss: "Masquer" },
  en: { title: "Welcome to geoVINUM",      sub: "3 steps to make the most of your subscription.",  dismiss: "Hide" },
};

export default function AmateurOnboardingChecklist() {
  const { lang } = useT();
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const DISMISS_KEY = user ? `amateur_onboarding_dismissed_${user.id}` : null;

  useEffect(() => {
    if (!user) return;
    if (DISMISS_KEY && localStorage.getItem(DISMISS_KEY) === "1") setDismissed(true);
    api.get("/amateur/onboarding-status").then((r) => setStatus(r.data)).catch(() => setStatus(null));
  }, [user, DISMISS_KEY]);

  // Show only for Amateur/Pro subscribers or admins, and only until fully complete.
  if (!user) return null;
  const eligible = user.subscription_active || user.role === "admin";
  if (!eligible) return null;
  if (!status || dismissed || status.all_done) return null;

  const meta = lang === "fr" ? HEADER.fr : HEADER.en;
  const pct = Math.round((status.completed / status.total) * 100);

  const onDismiss = () => {
    if (DISMISS_KEY) localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    <div className="mb-6 rounded-2xl bg-gradient-to-br from-[#FFF8FB] via-white to-[#FBF7F3] border border-[#E5276A]/20 p-5 md:p-6 relative overflow-hidden"
         data-testid="amateur-onboarding-checklist">
      <button
        onClick={onDismiss}
        data-testid="amateur-onboarding-dismiss"
        aria-label={meta.dismiss}
        className="absolute top-3 right-3 w-7 h-7 rounded-full text-[#8B95A1] hover:bg-[#FCE4ED] hover:text-[#E5276A] flex items-center justify-center transition-colors"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-center gap-2">
        <span className="p-1.5 rounded-lg bg-[#E5276A] text-white shrink-0">
          <Sparkles className="w-4 h-4" />
        </span>
        <div className="pr-8">
          <h2 className="font-display text-xl text-[#1F3A5F] leading-tight">{meta.title}</h2>
          <p className="text-sm text-[#4A5568] mt-0.5">{meta.sub}</p>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <div className="flex-1 h-2 rounded-full bg-[#1F3A5F]/10 overflow-hidden">
          <div
            data-testid="amateur-onboarding-progressbar"
            className="h-full bg-gradient-to-r from-[#E5276A] to-[#F9C548] rounded-full transition-[width] duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="text-xs font-bold text-[#1F3A5F] tabular-nums shrink-0">
          {status.completed}/{status.total}
        </span>
      </div>

      <ol className="mt-5 space-y-2">
        {status.steps.map((s, idx) => {
          const label = LABELS[s.key]?.[lang === "fr" ? "fr" : "en"] || { title: s.key, body: "" };
          const Icon = ICONS[s.key] || Circle;
          const progress = s.progress; // {current, target} for step 1
          return (
            <li key={s.key}>
              <Link
                to={s.cta_url}
                data-testid={`amateur-onboarding-step-${s.key}`}
                className={`group flex items-center gap-3 rounded-xl border p-3 transition-colors ${
                  s.done
                    ? "border-[#4CAF50]/30 bg-[#F1F8F1] hover:bg-[#E9F4E9]"
                    : "border-[#1F3A5F]/10 bg-white hover:border-[#E5276A]/40 hover:bg-[#FFF8FB]"
                }`}
              >
                <span
                  className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                    s.done ? "bg-[#4CAF50] text-white" : "bg-[#FCE4ED] text-[#E5276A]"
                  }`}
                >
                  {s.done ? <Check className="w-4 h-4" strokeWidth={3} /> : <Icon className="w-4 h-4" />}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-[#8B95A1]">
                      {idx + 1}.
                    </span>
                    <span className={`font-display text-sm ${s.done ? "text-[#4A5568] line-through decoration-2 decoration-[#4CAF50]/60" : "text-[#1F3A5F]"}`}>
                      {label.title}
                    </span>
                    {progress && !s.done && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FCE4ED] text-[#E5276A] font-bold tabular-nums">
                        {progress.current}/{progress.target}
                      </span>
                    )}
                  </div>
                  {!s.done && (
                    <div className="text-xs text-[#4A5568] mt-0.5">{label.body}</div>
                  )}
                </div>
                {!s.done && (
                  <ChevronRight className="w-4 h-4 text-[#8B95A1] group-hover:text-[#E5276A] group-hover:translate-x-0.5 transition-all shrink-0" />
                )}
              </Link>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
